/**
 * ScutaEntryDetail — Single SCUTA entry view.
 *
 * Archival, observational. Shows the full record of a completed activity.
 * No reactions, no comments, no engagement.
 *
 * Host can: edit draft, publish, attach photos.
 * Admin can: moderate institutional entries.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import {
  ArrowLeft, Clock, MapPin, User, Building, Users, Landmark,
  Image, FileText, Check, X, Edit2, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: { label: 'Draft', color: '#9AA3AF', bg: '#1E2936' },
  pending: { label: 'Pending Review', color: '#C2A45E', bg: '#2A2415' },
  approved: { label: 'Published', color: '#7A8F5B', bg: '#1A2315' },
  rejected: { label: 'Rejected', color: '#E87070', bg: '#2A1515' },
  archived: { label: 'Archived', color: '#9AA3AF', bg: '#1E2936' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default function ScutaEntryDetail() {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editDesc, setEditDesc] = useState('');

  useEffect(() => {
    loadEntry();
  }, [entryId]);

  // Force dark background on body for SCUTA pages
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#0B0F14';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  const loadEntry = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/scuta/entries/${entryId}`);
      if (res.ok) {
        const data = await res.json();
        setEntry(data);
        setEditDesc(data.description || '');
      }
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  const isHost = user?.user_id === entry?.host_id;
  const isFounder = user?.role === 'founder';
  const canEdit = isHost && (entry?.status === 'draft' || entry?.status === 'pending');
  const canPublish = isHost && entry?.status === 'draft' && !entry?.is_institutional;
  const canModerate = (isFounder || false) && entry?.status === 'pending';

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/scuta/entries/${entryId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ description: editDesc }),
      });
      if (res.ok) {
        toast.success('Entry updated');
        setEditing(false);
        loadEntry();
      }
    } catch {
      toast.error('Failed to update');
    }
  };

  const handlePublish = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/scuta/entries/${entryId}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast.success('Entry published to SCUTA');
        loadEntry();
      }
    } catch {
      toast.error('Failed to publish');
    }
  };

  const handleModerate = async (action) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/scuta/entries/${entryId}/moderate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        toast.success(`Entry ${action}d`);
        loadEntry();
      }
    } catch {
      toast.error('Failed to moderate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#C2A45E' }} />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0B0F14' }}>
        <p style={{ color: '#9AA3AF' }}>Entry not found.</p>
      </div>
    );
  }

  const statusInfo = STATUS_STYLES[entry.status] || STATUS_STYLES.draft;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F14' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: '#0B0F14', borderBottom: '1px solid #1E2936' }}>
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/scuta/entries')}
              className="p-2 rounded-full hover:opacity-70"
              data-testid="entry-detail-back-btn"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#E6E9EE' }} />
            </button>
            <span className="text-xs tracking-widest uppercase" style={{ color: '#9AA3AF' }}>
              SCUTA Record
            </span>
          </div>
          {canEdit && (
            <button
              onClick={() => setEditing(!editing)}
              className="p-2 rounded-full hover:opacity-70"
              data-testid="entry-edit-btn"
            >
              <Edit2 className="w-4 h-4" style={{ color: '#C2A45E' }} />
            </button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        {/* Status badge */}
        <div className="flex items-center gap-2">
          <span
            className="px-3 py-1 rounded-full text-xs font-semibold"
            style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
            data-testid="entry-detail-status"
          >
            {statusInfo.label}
          </span>
          {entry.is_institutional && (
            <span
              className="px-2 py-0.5 rounded text-[10px] flex items-center gap-1"
              style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}
            >
              <Building className="w-3 h-3" /> Institutional
            </span>
          )}
        </div>

        {/* Title */}
        <h1
          className="text-2xl font-bold leading-tight"
          style={{ color: '#E6E9EE' }}
          data-testid="entry-detail-title"
        >
          {entry.title}
        </h1>

        {/* Meta card */}
        <Card style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}>
          <div className="p-4 space-y-3">
            {entry.completion_date && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 flex-shrink-0" style={{ color: '#C2A45E' }} />
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9AA3AF' }}>Completed</p>
                  <p className="text-sm" style={{ color: '#E6E9EE' }}>
                    {formatDate(entry.completion_date)}
                  </p>
                </div>
              </div>
            )}
            {entry.start_time && (
              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 flex-shrink-0" style={{ color: '#9AA3AF' }} />
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9AA3AF' }}>Event Time</p>
                  <p className="text-sm" style={{ color: '#E6E9EE' }}>
                    {formatDate(entry.start_time)}
                    {' at '}
                    {formatTime(entry.start_time)}
                    {entry.end_time && ` - ${formatTime(entry.end_time)}`}
                  </p>
                </div>
              </div>
            )}
            {entry.location && (
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9AA3AF' }}>Location</p>
                  <p className="text-sm" style={{ color: '#E6E9EE' }}>{entry.location}</p>
                </div>
              </div>
            )}
            {entry.host_name && (
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 flex-shrink-0" style={{ color: '#9AA3AF' }} />
                <div>
                  <p className="text-[10px] uppercase tracking-wide" style={{ color: '#9AA3AF' }}>Host</p>
                  <p className="text-sm" style={{ color: '#E6E9EE' }}>{entry.host_name}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Associations */}
        {(entry.institution_id || entry.commons_id || entry.group_id) && (
          <div className="flex flex-wrap gap-2">
            {entry.institution_id && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
                <Building className="w-3.5 h-3.5" /> Institution
              </span>
            )}
            {entry.commons_id && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
                <Landmark className="w-3.5 h-3.5" /> Commons
              </span>
            )}
            {entry.group_id && (
              <span className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
                <Users className="w-3.5 h-3.5" /> Group
              </span>
            )}
          </div>
        )}

        {/* Description */}
        <Card style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}>
          <div className="p-4">
            {editing ? (
              <div className="space-y-3">
                <textarea
                  value={editDesc}
                  onChange={e => setEditDesc(e.target.value)}
                  className="w-full rounded-lg p-3 text-sm leading-relaxed resize-none"
                  style={{
                    backgroundColor: '#0B0F14',
                    color: '#E6E9EE',
                    border: '1px solid #1E2936',
                    minHeight: 120,
                  }}
                  data-testid="entry-edit-description"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSaveEdit}
                    size="sm"
                    className="rounded-full text-xs"
                    style={{ backgroundColor: '#C2A45E', color: '#0B0F14' }}
                    data-testid="entry-save-btn"
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => { setEditing(false); setEditDesc(entry.description || ''); }}
                    variant="outline"
                    size="sm"
                    className="rounded-full text-xs"
                    style={{ borderColor: '#1E2936', color: '#9AA3AF' }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p
                className="text-sm leading-relaxed whitespace-pre-line"
                style={{ color: '#E6E9EE' }}
                data-testid="entry-detail-description"
              >
                {entry.description || 'No description recorded.'}
              </p>
            )}
          </div>
        </Card>

        {/* Photos */}
        {(entry.photos || []).length > 0 && (
          <Card style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}>
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-widest mb-3" style={{ color: '#9AA3AF' }}>
                Photos
              </p>
              <div className="grid grid-cols-3 gap-2">
                {entry.photos.map((photoId, i) => (
                  <div
                    key={photoId}
                    className="aspect-square rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: '#1E2936' }}
                    data-testid={`entry-photo-${i}`}
                  >
                    <Image className="w-6 h-6" style={{ color: '#9AA3AF' }} />
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* Host actions */}
        {canPublish && (
          <Card style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}>
            <div className="p-4">
              <p className="text-xs mb-3" style={{ color: '#9AA3AF' }}>
                This entry is a draft. Publish to make it visible in the SCUTA archive.
              </p>
              <Button
                onClick={handlePublish}
                size="sm"
                className="rounded-full text-xs flex items-center gap-1.5"
                style={{ backgroundColor: '#C2A45E', color: '#0B0F14' }}
                data-testid="entry-publish-btn"
              >
                <Send className="w-3 h-3" /> Publish to SCUTA
              </Button>
            </div>
          </Card>
        )}

        {/* Moderation actions */}
        {canModerate && (
          <Card style={{ backgroundColor: '#111820', border: '1px solid #C2A45E30' }}>
            <div className="p-4">
              <p className="text-xs mb-3" style={{ color: '#C2A45E' }}>
                This institutional entry is awaiting review.
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleModerate('approve')}
                  size="sm"
                  className="rounded-full text-xs flex items-center gap-1"
                  style={{ backgroundColor: '#7A8F5B', color: '#fff' }}
                  data-testid="entry-approve-btn"
                >
                  <Check className="w-3 h-3" /> Approve
                </Button>
                <Button
                  onClick={() => handleModerate('reject')}
                  variant="outline"
                  size="sm"
                  className="rounded-full text-xs flex items-center gap-1"
                  style={{ borderColor: '#1E2936', color: '#9AA3AF' }}
                  data-testid="entry-reject-btn"
                >
                  <X className="w-3 h-3" /> Reject
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Source reference */}
        {entry.activity_id && (
          <div className="pt-2" style={{ borderTop: '1px solid #1E2936' }}>
            <p className="text-[10px] tracking-widest uppercase" style={{ color: '#9AA3AF' }}>
              Source: Activity Record
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
